import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine } from "recharts";
import { api, errMsg, formatDate } from "../lib/api";
import { toast } from "sonner";

const AXIS = { stroke: "#64748b", fontSize: 11 };
const TIP = { contentStyle: { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 } };
const STATUS = { completed: "text-emerald-300", active: "text-blue-300", upcoming: "text-slate-400" };

function Velocity({ sprints, avg }) {
  const data = sprints.map((s) => ({ name: `S${s.sprint_number}`, Planned: s.planned_points, Completed: s.completed_points }));
  return (
    <div className="pc-card p-5" data-testid="velocity-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-blue-300/80">Velocity (story points)</div>
        <div className="text-xs text-slate-400" data-testid="average-velocity">Avg completed: <span className="text-white font-semibold">{avg}</span></div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data}>
          <CartesianGrid stroke="#1e293b" vertical={false} />
          <XAxis dataKey="name" {...AXIS} /><YAxis {...AXIS} allowDecimals={false} />
          <Tooltip {...TIP} cursor={{ fill: "#1e293b55" }} /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Planned" fill="#334155" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Completed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          {avg > 0 && <ReferenceLine y={avg} stroke="#10b981" strokeDasharray="4 4" />}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Burndown({ sprints, selected, setSelected }) {
  const s = sprints.find((x) => x.sprint_number === selected) || sprints[0];
  const data = (s?.burndown || []).map((d) => ({ ...d, day: formatDate(d.date) }));
  return (
    <div className="pc-card p-5" data-testid="burndown-chart">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="text-xs uppercase tracking-widest text-blue-300/80">Burndown</div>
        <select value={s?.sprint_number || ""} onChange={(e) => setSelected(Number(e.target.value))} data-testid="burndown-sprint-select"
          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-200">
          {sprints.map((x) => <option key={x.sprint_number} value={x.sprint_number}>{`Sprint ${x.sprint_number}`}</option>)}
        </select>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data}>
          <CartesianGrid stroke="#1e293b" vertical={false} />
          <XAxis dataKey="day" {...AXIS} interval="preserveStartEnd" /><YAxis {...AXIS} allowDecimals={false} />
          <Tooltip {...TIP} /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="linear" dataKey="ideal" name="Ideal" stroke="#64748b" strokeDasharray="5 5" dot={false} />
          <Line type="linear" dataKey="remaining" name="Remaining" stroke="#3b82f6" strokeWidth={2} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function SprintTable({ sprints }) {
  return (
    <div className="pc-card overflow-x-auto mt-4" data-testid="sprints-table">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
          <tr><th className="p-3">Sprint</th><th className="p-3">Dates</th><th className="p-3">Stories</th><th className="p-3">Points done / planned</th><th className="p-3">Status</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60">
          {sprints.map((s) => (
            <tr key={s.sprint_number} data-testid={`sprint-row-${s.sprint_number}`}>
              <td className="p-3 text-white font-medium">Sprint {s.sprint_number}</td>
              <td className="p-3 text-slate-300">{formatDate(s.start_date)} – {formatDate(s.end_date)}</td>
              <td className="p-3 text-slate-300">{s.completed_count}/{s.story_count}</td>
              <td className="p-3 mono text-slate-200" data-testid={`sprint-points-${s.sprint_number}`}>{s.completed_points} / {s.planned_points}</td>
              <td className={`p-3 capitalize ${STATUS[s.status]}`} data-testid={`sprint-status-${s.sprint_number}`}>{s.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SprintsPanel({ projectId, refreshKey }) {
  const [d, setD] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/sprints/stats`).then((r) => {
      setD(r.data);
      setSelected((cur) => cur ?? (r.data.sprints.find((s) => s.status === "active") || r.data.sprints[0])?.sprint_number ?? null);
    }).catch((e) => toast.error(errMsg(e)));
  }, [projectId, refreshKey]);

  if (!d) return <div className="text-slate-400 mb-6" data-testid="sprints-loading">Loading sprints…</div>;
  if (!d.sprint_config || d.sprints.length === 0) {
    return <div className="pc-card p-10 text-center text-slate-400 mb-6" data-testid="sprints-empty">No sprints yet. Click "Plan sprints" to add sprint blocks to this project.</div>;
  }
  return (
    <div className="mb-6" data-testid="sprints-panel">
      <div className="grid lg:grid-cols-2 gap-4">
        <Velocity sprints={d.sprints} avg={d.average_velocity} />
        <Burndown sprints={d.sprints} selected={selected} setSelected={setSelected} />
      </div>
      <SprintTable sprints={d.sprints} />
    </div>
  );
}
